import { useEffect, useState } from "react";
import { toast } from "sonner";
import api, { formatApiErrorDetail, fileUrl } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { fmtDate } from "@/lib/constants";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import DocViewer from "@/components/DocViewer";
import ActivityFlowActions from "@/components/ActivityFlowActions";
import { Checkbox } from "@/components/ui/checkbox";
import { FileText, Sparkle, PaperPlaneTilt, PencilSimple, Trash, FloppyDisk, X, Printer } from "@phosphor-icons/react";

const DOC_LABEL = { deviz_bom: "Deviz / BOM", bon_consum: "Bon de consum", comanda: "Comandă materii prime" };
function DocView({ doc, onClose, onSaved }) {
  const { hasRole } = useAuth();
  const [edit, setEdit] = useState(false);
  const [lines, setLines] = useState(doc.lines);
  const [vat, setVat] = useState(doc.vat_rate);
  const [emailFor, setEmailFor] = useState(false);
  const [recipient, setRecipient] = useState("");
  const isBom = doc.doc_type === "deviz_bom";
  const canEdit = hasRole("pm", "inginer", "admin");

  const setCell = (i, k, v) => setLines((ls) => ls.map((l, idx) => (idx === i ? { ...l, [k]: v } : l)));

  const save = async () => {
    try {
      const { data } = await api.put(`/documents/${doc.id}`, {
        lines: lines.map((l) => ({ ...l, qty: Number(l.qty) || 0, unit_weight: Number(l.unit_weight) || 0, unit_price: Number(l.unit_price) || 0 })),
        vat_rate: Number(vat) || 0,
      });
      toast.success("Document salvat (recalculat)");
      setEdit(false);
      setLines(data.lines);
      onSaved(data);
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const sendEmail = async () => {
    if (!recipient) return;
    try {
      const { data } = await api.post(`/documents/${doc.id}/send-email`, { recipient });
      if (data.ok) toast.success(`Trimis către ${recipient}`); else toast.error("Trimitere eșuată");
      setEmailFor(false);
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const c = doc.company || {};

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-5xl">
        <DialogHeader>
          <DialogTitle className="flex items-center justify-between pr-8">
            <span>{DOC_LABEL[doc.doc_type]} · {doc.number}</span>
            <span className="flex gap-2">
              {canEdit && !edit && <Button size="sm" variant="outline" onClick={() => setEdit(true)} data-testid="doc-edit-btn"><PencilSimple size={14} className="mr-1" /> Editează</Button>}
              {edit && <Button size="sm" onClick={save} data-testid="doc-save-btn"><FloppyDisk size={14} className="mr-1" /> Salvează</Button>}
              <Button size="sm" variant="outline" onClick={() => window.print()} data-testid="doc-print-btn"><Printer size={14} className="mr-1" /> PDF</Button>
              <Button size="sm" variant="outline" onClick={() => setEmailFor(true)} data-testid="doc-email-btn"><PaperPlaneTilt size={14} className="mr-1" /> Trimite</Button>
            </span>
          </DialogTitle>
        </DialogHeader>

        {/* pixel-perfect document */}
        <div className="max-h-[70vh] overflow-y-auto rounded-lg bg-white p-8 text-[13px] text-neutral-800 printable" data-testid="doc-view">
          <div className="flex items-start justify-between border-b-2 border-neutral-800 pb-4">
            <div>
              <div className="text-lg font-extrabold text-neutral-900">{c.name}</div>
              <div className="text-neutral-600">Reg. Com.: {c.reg_com} · CIF: {c.cif}</div>
              <div className="text-neutral-600">{c.address}, {c.city}, jud. {c.county}</div>
              <div className="text-neutral-600">IBAN: {c.iban} · {c.bank}</div>
            </div>
            <div className="text-right">
              <div className="text-xl font-extrabold uppercase text-neutral-900">{DOC_LABEL[doc.doc_type]}</div>
              <div className="mt-1 font-mono font-bold">{doc.number}</div>
              <div className="text-neutral-600">Data: {fmtDate(doc.date)}</div>
            </div>
          </div>
          <div className="py-3 font-semibold text-neutral-900">{doc.title}</div>

          <table className="w-full border-collapse text-[12px]">
            <thead>
              <tr className="bg-neutral-100 text-left">
                <th className="border border-neutral-300 p-2">Nr</th>
                <th className="border border-neutral-300 p-2">Articol</th>
                {isBom && <th className="border border-neutral-300 p-2">Cod desen</th>}
                <th className="border border-neutral-300 p-2">Cant.</th>
                <th className="border border-neutral-300 p-2">UM</th>
                {isBom && <th className="border border-neutral-300 p-2">Material</th>}
                {isBom && <th className="border border-neutral-300 p-2">Greut./buc</th>}
                {isBom && <th className="border border-neutral-300 p-2">Greut. totală</th>}
                {!isBom && <th className="border border-neutral-300 p-2">Preț unitar</th>}
                <th className="border border-neutral-300 p-2">Valoare fără TVA</th>
                <th className="border border-neutral-300 p-2">TVA</th>
                <th className="border border-neutral-300 p-2">Valoare cu TVA</th>
              </tr>
            </thead>
            <tbody>
              {lines.map((l, i) => (
                <tr key={i}>
                  <td className="border border-neutral-300 p-2">{i + 1}</td>
                  <td className="border border-neutral-300 p-2">
                    {edit ? <input className="w-full bg-yellow-50 px-1" value={l.name} onChange={(e) => setCell(i, "name", e.target.value)} /> : <><b>{l.name}</b><div className="text-[10px] text-neutral-500">{l.category}</div></>}
                  </td>
                  {isBom && <td className="border border-neutral-300 p-2">{l.drawing_code}</td>}
                  <td className="border border-neutral-300 p-2">
                    {edit ? <input className="w-16 bg-yellow-50 px-1" value={l.qty} onChange={(e) => setCell(i, "qty", e.target.value)} /> : l.qty}
                  </td>
                  <td className="border border-neutral-300 p-2">{l.um}</td>
                  {isBom && <td className="border border-neutral-300 p-2">{l.material}</td>}
                  {isBom && <td className="border border-neutral-300 p-2">{edit ? <input className="w-16 bg-yellow-50 px-1" value={l.unit_weight} onChange={(e) => setCell(i, "unit_weight", e.target.value)} /> : `${l.unit_weight} kg`}</td>}
                  {isBom && <td className="border border-neutral-300 p-2">{l.total_weight} kg</td>}
                  {!isBom && <td className="border border-neutral-300 p-2">{edit ? <input className="w-20 bg-yellow-50 px-1" value={l.unit_price} onChange={(e) => setCell(i, "unit_price", e.target.value)} /> : `${l.unit_price} lei`}</td>}
                  <td className="border border-neutral-300 p-2">{l.value_no_vat} lei</td>
                  <td className="border border-neutral-300 p-2">{l.vat} lei</td>
                  <td className="border border-neutral-300 p-2 font-semibold">{l.value_with_vat} lei</td>
                </tr>
              ))}
            </tbody>
          </table>

          <div className="mt-4 flex justify-end">
            <table className="text-[12px]">
              <tbody>
                {isBom && <tr><td className="p-1 pr-6 text-neutral-600">Greutate totală:</td><td className="p-1 text-right font-bold">{doc.total_weight} kg</td></tr>}
                <tr><td className="p-1 pr-6 text-neutral-600">Total fără TVA:</td><td className="p-1 text-right font-bold">{doc.total_no_vat} lei</td></tr>
                <tr><td className="p-1 pr-6 text-neutral-600">TVA ({edit ? <input className="w-12 bg-yellow-50 px-1" value={vat} onChange={(e) => setVat(e.target.value)} /> : vat}%):</td><td className="p-1 text-right font-bold">{doc.total_vat} lei</td></tr>
                <tr className="border-t-2 border-neutral-800"><td className="p-1 pr-6 font-bold">TOTAL de plată:</td><td className="p-1 text-right text-base font-extrabold">{doc.total_with_vat} lei</td></tr>
              </tbody>
            </table>
          </div>

          <div className="mt-8 flex justify-between text-[11px] text-neutral-600">
            <div>
              <div className="mb-6 font-semibold text-neutral-800">{isBom ? "Întocmit / Verificat" : "Document întocmit de"}</div>
              <div className="border-t border-neutral-400 pt-1">Semnătură & ștampilă</div>
            </div>
            <div className="text-right">
              <div className="mb-6 font-semibold text-neutral-800">Primit</div>
              <div className="border-t border-neutral-400 pt-1">Semnătură</div>
            </div>
          </div>
        </div>

        <Dialog open={emailFor} onOpenChange={setEmailFor}>
          <DialogContent>
            <DialogHeader><DialogTitle>Trimite document pe email</DialogTitle></DialogHeader>
            <Input type="email" placeholder="destinatar@email.ro" value={recipient} onChange={(e) => setRecipient(e.target.value)} data-testid="doc-email-recipient" />
            <DialogFooter><Button onClick={sendEmail} data-testid="doc-email-send">Trimite (Resend)</Button></DialogFooter>
          </DialogContent>
        </Dialog>
      </DialogContent>
    </Dialog>
  );
}

export default function DocumentsPanel({ projectId }) {
  const { hasRole } = useAuth();
  const [docs, setDocs] = useState([]);
  const [folderFiles, setFolderFiles] = useState([]);
  const [viewing, setViewing] = useState(null);
  const [viewFile, setViewFile] = useState(null);
  const [gen, setGen] = useState("");
  const [fsel, setFsel] = useState([]);
  const [fName, setFName] = useState("");
  const [fType, setFType] = useState("__all");
  const [fUser, setFUser] = useState("__all");
  const [fFrom, setFFrom] = useState("");
  const [fTo, setFTo] = useState("");
  const toggleFsel = (id) => setFsel((s) => (s.includes(id) ? s.filter((x) => x !== id) : [...s, id]));

  const load = () => api.get(`/projects/${projectId}/documents`).then((r) => setDocs(r.data)).catch(() => {});
  const loadFolder = () => api.get(`/projects/${projectId}/folder`).then((r) => setFolderFiles(r.data)).catch(() => {});
  useEffect(() => { load(); loadFolder(); /* eslint-disable-next-line */ }, [projectId]);

  const canGen = hasRole("pm", "inginer", "admin");
  const canDelFolder = true;
  const FOLDER_CATS = {
    contract: "Contract", oferta: "Ofertă", factura: "Factură", proforma: "Proformă",
    comanda: "Comandă", aviz: "Aviz", cmr: "CMR", desen: "Desen", fisa_proiect: "Fișă proiect",
    pv: "Proces-verbal", certificat: "Certificat", dovada: "Dovadă încasare", nota_santier: "Notă șantier",
    email: "Email", altul: "Altul",
  };
  const delFolder = async (f) => {
    if (!window.confirm(`Ștergi documentul „${f.name}" din dosarul digital?`)) return;
    try { await api.delete(`/projects/${projectId}/folder/${f.id}`); toast.success("Document șters"); loadFolder(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const generate = async (doc_type) => {
    setGen(doc_type);
    try {
      const { data } = await api.post(`/projects/${projectId}/documents/generate`, { doc_type });
      toast.success(`${DOC_LABEL[doc_type]} generat (${data.number})`);
      setDocs((d) => [data, ...d]);
      setViewing(data);
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setGen(""); }
  };

  const del = async (id) => { await api.delete(`/documents/${id}`); load(); };

  const bonFromInventory = async () => {
    setGen("bon_inv");
    try {
      const { data } = await api.post(`/projects/${projectId}/documents/bon-from-inventory`);
      toast.success(`Bon de consum generat din gestiune (${data.number})`);
      setDocs((d) => [data, ...d]);
      setViewing(data);
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setGen(""); }
  };

  const fTypes = Array.from(new Set((folderFiles || []).map((f) => f.category).filter(Boolean)));
  const fUsers = Array.from(new Set((folderFiles || []).map((f) => f.uploaded_by_name || f.uploaded_by_email).filter(Boolean)));
  const filteredFolder = (folderFiles || []).filter((f) => {
    if (fName && !(f.name || "").toLowerCase().includes(fName.toLowerCase())) return false;
    if (fType !== "__all" && f.category !== fType) return false;
    if (fUser !== "__all" && (f.uploaded_by_name || f.uploaded_by_email) !== fUser) return false;
    if (fFrom && f.created_at && f.created_at.slice(0, 10) < fFrom) return false;
    if (fTo && f.created_at && f.created_at.slice(0, 10) > fTo) return false;
    return true;
  });

  return (
    <div className="rounded-xl border border-border bg-card p-6" data-testid="documents-panel">
      {canGen && (
        <div className="mb-4 flex flex-wrap gap-2">
          <Button size="sm" onClick={() => generate("deviz_bom")} disabled={gen === "deviz_bom"} data-testid="gen-bom-btn">
            <Sparkle size={14} weight="fill" className="mr-1 text-violet-300" /> {gen === "deviz_bom" ? "Se generează (AI)…" : "Generează Deviz/BOM (AI)"}
          </Button>
          <Button size="sm" variant="outline" onClick={() => generate("bon_consum")} disabled={gen === "bon_consum"} data-testid="gen-bon-btn">
            <FileText size={14} className="mr-1" /> Generează Bon de consum
          </Button>
          <Button size="sm" variant="outline" onClick={() => generate("comanda")} disabled={gen === "comanda"} data-testid="gen-comanda-btn">
            <FileText size={14} className="mr-1" /> {gen === "comanda" ? "Se generează (AI)…" : "Generează Comandă materii prime"}
          </Button>
          <Button size="sm" variant="outline" onClick={bonFromInventory} disabled={gen === "bon_inv"} data-testid="gen-bon-inv-btn">
            <FileText size={14} className="mr-1" /> {gen === "bon_inv" ? "Se generează…" : "Bon din gestiune proiect"}
          </Button>
        </div>
      )}
      <div className="space-y-2">
        {docs.map((d) => (
          <div key={d.id} className="flex items-center gap-3 rounded-lg border border-border px-3 py-2.5" data-testid={`doc-${d.id}`}>
            <FileText size={18} className="text-blue-400" />
            <div className="flex-1">
              <span className="text-sm font-semibold">{DOC_LABEL[d.doc_type]}</span>
              <span className="ml-2 font-mono text-xs text-muted-foreground">{d.number}</span>
              {d.status === "trimis" && <span className="ml-2 rounded bg-emerald-500/15 px-1.5 py-0.5 text-[10px] text-emerald-400">Trimis</span>}
            </div>
            <span className="text-xs text-muted-foreground">{d.total_with_vat} lei</span>
            <a href={`${process.env.REACT_APP_BACKEND_URL}/api/documents/${d.id}/export.xlsx?auth=${localStorage.getItem("hgs_token")}`} target="_blank" rel="noreferrer" className="text-xs text-emerald-400 hover:underline" data-testid={`export-xlsx-${d.id}`}>Excel</a>
            <a href={`${process.env.REACT_APP_BACKEND_URL}/api/documents/${d.id}/export.docx?auth=${localStorage.getItem("hgs_token")}`} target="_blank" rel="noreferrer" className="text-xs text-sky-400 hover:underline" data-testid={`export-docx-${d.id}`}>Word</a>
            <Button size="sm" variant="outline" className="h-7 text-xs" onClick={() => setViewing(d)} data-testid={`view-doc-${d.id}`}>Vezi</Button>
            {canGen && <button onClick={() => del(d.id)} className="text-muted-foreground hover:text-red-400"><Trash size={15} /></button>}
          </div>
        ))}
        {docs.length === 0 && <p className="text-sm text-muted-foreground">Niciun document generat. Generează automat din datele proiectului.</p>}
      </div>

      <div className="mt-6 border-t border-border pt-5" data-testid="documents-folder-section">
        <div className="mb-2 flex flex-wrap items-center justify-between gap-2">
          <h3 className="text-xs font-bold uppercase tracking-wide text-muted-foreground">Toate documentele proiectului ({filteredFolder.length}{filteredFolder.length !== folderFiles.length ? `/${folderFiles.length}` : ""})</h3>
          {fsel.length > 0 && <ActivityFlowActions projectId={projectId} projectCode="documente" initialDocs={fsel} forwardOnly label={`Trimite mai departe (${fsel.length})`} onDone={() => setFsel([])} />}
        </div>
        <div className="mb-3 flex flex-wrap items-center gap-2" data-testid="docpanel-filters">
          <Input value={fName} onChange={(e) => setFName(e.target.value)} placeholder="Caută în denumire…" className="h-8 w-44" data-testid="docpanel-f-name" />
          <select value={fType} onChange={(e) => setFType(e.target.value)} className="h-8 rounded-md border border-border bg-background px-2 text-xs" data-testid="docpanel-f-type"><option value="__all">Toate tipurile</option>{fTypes.map((t) => <option key={t} value={t}>{FOLDER_CATS[t] || t}</option>)}</select>
          <select value={fUser} onChange={(e) => setFUser(e.target.value)} className="h-8 rounded-md border border-border bg-background px-2 text-xs" data-testid="docpanel-f-user"><option value="__all">Toți utilizatorii</option>{fUsers.map((u) => <option key={u} value={u}>{u}</option>)}</select>
          <Input type="date" value={fFrom} onChange={(e) => setFFrom(e.target.value)} className="h-8 w-36" data-testid="docpanel-f-from" title="De la data" />
          <Input type="date" value={fTo} onChange={(e) => setFTo(e.target.value)} className="h-8 w-36" data-testid="docpanel-f-to" title="Până la data" />
          {(fName || fType !== "__all" || fUser !== "__all" || fFrom || fTo) && <button onClick={() => { setFName(""); setFType("__all"); setFUser("__all"); setFFrom(""); setFTo(""); }} className="text-xs text-muted-foreground underline hover:text-foreground" data-testid="docpanel-f-clear">Resetează</button>}
        </div>
        <div className="space-y-2">
          {filteredFolder.map((f) => (
            <div key={f.id} className="flex items-center gap-3 rounded-lg border border-border px-3 py-2.5" data-testid={`folder-doc-${f.id}`}>
              <Checkbox checked={fsel.includes(f.id)} onCheckedChange={() => toggleFsel(f.id)} data-testid={`docpanel-sel-${f.id}`} />
              <FileText size={18} className="shrink-0 text-blue-400" />
              <div className="min-w-0 flex-1">
                <div className="truncate text-sm font-medium">{f.name}</div>
                <div className="text-[11px] text-muted-foreground">{fmtDate(f.created_at)} · {f.uploaded_by_name || f.uploaded_by_email || "—"}{f.folder ? ` · ${f.folder}` : ""}</div>
              </div>
              <span className="rounded bg-white/5 px-1.5 py-0.5 text-[11px] text-muted-foreground">{FOLDER_CATS[f.category] || f.category}</span>
              <button onClick={() => setViewFile(f)} className="text-xs text-primary hover:underline" data-testid={`folder-doc-open-${f.id}`}>Deschide</button>
              <a href={fileUrl(f.storage_path)} download={f.name} className="text-xs text-muted-foreground hover:underline" data-testid={`folder-doc-dl-${f.id}`}>Descarcă</a>
              {canDelFolder && <button onClick={() => delFolder(f)} className="text-muted-foreground hover:text-red-400" title="Șterge documentul" data-testid={`folder-doc-del-${f.id}`}><Trash size={15} /></button>}
            </div>
          ))}
          {filteredFolder.length === 0 && <p className="text-sm text-muted-foreground">{folderFiles.length ? "Niciun document nu corespunde filtrelor." : "Niciun document încărcat în dosar încă."}</p>}
        </div>
      </div>

      {viewing && <DocView doc={viewing} onClose={() => setViewing(null)} onSaved={(d) => { setViewing(d); load(); }} />}
      <DocViewer file={viewFile} onClose={() => setViewFile(null)} />
    </div>
  );
}
